package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.data.dto.ProfileCardDto
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import com.arena_web.data.dto.UserCardDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// Figma tokens — Bring-Your-Card-to-Life popup (7714:174540).
// `list_client_row_hover` token in design = #09142d.
private const val DIALOG_BG = "#09142d"

// Faint section divider — gradient fades to make a soft hairline.
private const val SECTION_DIVIDER_GRADIENT =
    "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)"
private const val VERTICAL_DIVIDER_GRADIENT =
    "linear-gradient(180deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)"

// Position-badge surface — 24deg gradient over translucent blue. Used both
// for the large "next position" badge in My styles and the small per-card
// badges in the Active profile cards row.
private val BADGE_BG =
    AppGradients.InteractiveSecondary

/**
 * "Bring Your Card to Life" popup — Figma 7714:174540.
 *
 * Sections:
 *   1. **My styles** — pick a template, hit "Create Photo" to launch the AI
 *      avatar generation flow.
 *   2. **Active profile cards** — gallery rows in display order, drag to
 *      reorder. First entry is the user's main card.
 *
 * Cancel discards local reorder. Primary button ("Let's go !") commits the
 * new order through [onSaveOrder].
 */
@Composable
fun BringCardToLifePopup(
    isOpen: Boolean,
    onClose: () -> Unit,
    ownedTemplates: List<UserCardDto>,
    profileCards: List<ProfileCardDto>,
    sportName: String,
    activeTemplateId: String?,
    onCreatePhoto: (templateId: String) -> Unit,
    onSaveOrder: (orderedIds: List<String>) -> Unit,
    onLockedClick: (UserCardDto) -> Unit = {},
    /** Edit an existing gallery card — opens AI Avatar with that card's photo. */
    onEditCard: (ProfileCardDto) -> Unit = {},
    /** Delete a card from the gallery (the purchased template is kept). */
    onDeleteCardPhoto: (ProfileCardDto) -> Unit = {},
    /** Render as a mobile bottom sheet (Figma 8171:174894 / 8171:174820). */
    mobile: Boolean = false,
) {
    if (!isOpen) return

    // Card whose photo is pending a delete confirmation (Edit ▸ Delete).
    var deleteTarget by remember { mutableStateOf<ProfileCardDto?>(null) }

    // Default the selection to the user's currently-active template, falling
    // back to the first owned (purchasable) template. Locked tiles can't be
    // picked until purchased.
    val initialSelected = activeTemplateId
        ?: ownedTemplates.firstOrNull { it.isOwned }?.id
        ?: ownedTemplates.firstOrNull()?.id
    var selectedTemplateId by remember(initialSelected) { mutableStateOf(initialSelected) }
    var localOrder by remember(profileCards) { mutableStateOf(profileCards) }
    val dirty = remember(localOrder, profileCards) {
        localOrder.map { it.id } != profileCards.map { it.id }
    }

    // New-user flow: the backend ships a single default placeholder card (no
    // avatar). While that's the *only* card we hide the "Active profile cards"
    // section and "Create Photo" fills it in place. But once the user has more
    // than one card, every card is shown — including any leftover empty one —
    // so it can still be reordered or deleted (otherwise an avatar-less card
    // would silently hide the whole list).
    val isLoneEmptyPlaceholder =
        profileCards.size == 1 && profileCards.single().avatarUrl.isNullOrBlank()
    val showActiveCards = profileCards.isNotEmpty() && !isLoneEmptyPlaceholder

    // Card-creation no longer needs a save step (AvatarGenerationModal auto-saves
    // each generated avatar the moment it lands; the gallery here refreshes when
    // that modal is closed, via the caller's onAvatarConfirmed → loadProfile), so
    // the only thing left for the dialog to persist is the user's drag-to-reorder
    // changes. Dismiss → flush the new order if dirty, then close. This unifies
    // the backdrop tap and the explicit Close button.
    fun dismiss() {
        if (dirty) onSaveOrder(localOrder.map { it.id })
        onClose()
    }

    // Mobile → bottom sheet variant. Cancel / backdrop / drag discards the
    // local reorder; Save commits it (Figma 8171:174820).
    if (mobile) {
        BringCardToLifeSheet(
            templates = ownedTemplates,
            profileCards = profileCards,
            localOrder = localOrder,
            selectedTemplateId = selectedTemplateId,
            sportName = sportName,
            editingFirstCard = isLoneEmptyPlaceholder,
            showActiveCards = showActiveCards,
            onSelect = { selectedTemplateId = it },
            onLockedClick = onLockedClick,
            onCreatePhoto = { selectedTemplateId?.let { onCreatePhoto(it) } },
            onReorder = { localOrder = it },
            onEditCard = onEditCard,
            onRequestDelete = { deleteTarget = it },
            onCancel = onClose,
            onSave = {
                onSaveOrder(localOrder.map { it.id })
                onClose()
            },
        )
        DeletePhotoConfirm(
            target = deleteTarget,
            mobile = true,
            onConfirm = { card -> onDeleteCardPhoto(card); deleteTarget = null },
            onCancel = { deleteTarget = null },
        )
        return
    }

    // Backdrop — full-viewport overlay. Click outside dismisses; pending
    // reorder changes are auto-saved via `dismiss()` first.
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .styleModifier {
                property("background", "rgba(0, 0, 0, 0.6)")
            }
            .onClick { dismiss() },
        contentAlignment = Alignment.Center
    ) {
        // Dialog container — Figma 7714:174540.
        Column(
            modifier = Modifier
                .sMaxWidth(820)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(26)
                .sPadding(22)
                .onClick { it.stopPropagation() }
                .sGap(24)
                .styleModifier {
                    property("background", DIALOG_BG)
                    property("overflow", "hidden")
                }
        ) {
            val t = LocalAppStrings.current
            HeaderBlock()

            // ── My styles
            SectionHeader(title = t.cardLifeMyStyles, hint = t.cardLifeSelectToGenerate)
            MyStylesCardSelection(
                templates = ownedTemplates,
                profileCards = profileCards,
                selectedTemplateId = selectedTemplateId,
                sportName = sportName,
                editingFirstCard = isLoneEmptyPlaceholder,
                onSelect = { selectedTemplateId = it },
                onLockedClick = onLockedClick,
                onCreatePhoto = {
                    selectedTemplateId?.let { onCreatePhoto(it) }
                }
            )

            // ── Active profile cards
            if (showActiveCards) {
                SectionDivider()
                SectionHeader(title = t.cardLifeActiveCards, hint = t.cardLifeDragToReorder)
                ActiveCardsRow(
                    cards = localOrder,
                    sportName = sportName,
                    onReorder = { localOrder = it },
                    onEditCard = onEditCard,
                    onRequestDelete = { deleteTarget = it },
                )
            }

            // ── Footer — single Close button. Card creation now happens
            // automatically inside AvatarGenerationModal as each photo is
            // generated, so there's no separate Save step here. The dismiss()
            // above handles any pending reorder save.
            FooterButtons(onClose = { dismiss() })
        }
    }

    DeletePhotoConfirm(
        target = deleteTarget,
        mobile = false,
        onConfirm = { card -> onDeleteCardPhoto(card); deleteTarget = null },
        onCancel = { deleteTarget = null },
    )
}

// =============================================================================
// Mobile bottom-sheet variant — Figma 8171:174894 (My styles only) /
// 8171:174820 (with Active profile cards). Same data + callbacks as the
// desktop popup, re-flowed into the phone layout: title + divider, "My styles"
// (selected card | divider | template strip), full-width Create Photo, then the
// reorderable "Active profile cards" row and a Cancel / Save footer.
// =============================================================================
@Composable
private fun BringCardToLifeSheet(
    templates: List<UserCardDto>,
    profileCards: List<ProfileCardDto>,
    localOrder: List<ProfileCardDto>,
    selectedTemplateId: String?,
    sportName: String,
    editingFirstCard: Boolean,
    showActiveCards: Boolean,
    onSelect: (String) -> Unit,
    onLockedClick: (UserCardDto) -> Unit,
    onCreatePhoto: () -> Unit,
    onReorder: (List<ProfileCardDto>) -> Unit,
    onEditCard: (ProfileCardDto) -> Unit,
    onRequestDelete: (ProfileCardDto) -> Unit,
    onCancel: () -> Unit,
    onSave: () -> Unit,
) {
    val t = LocalAppStrings.current
    val selected = templates.firstOrNull { it.id == selectedTemplateId }
    val nextPosition = if (editingFirstCard) 1 else profileCards.size + 1

    BottomSheet(onDismiss = onCancel, contentGap = 16) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(16)
                .styleModifier {
                    property("flex", "1 1 auto")
                    property("min-height", "0")
                }
        ) {
            // Header — title + bottom hairline (no subtitle on mobile).
            Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
                SpanText(
                    t.cardLifeTitle,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(26)
                        .sLetterSpacing(0.2)
                        .color(AppColors.Text.Secondary)
                )
                SectionDivider()
            }

            // Scrollable body.
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex", "1 1 auto")
                        property("min-height", "0")
                        property("overflow-y", "auto")
                        property("overflow-x", "hidden")
                        property("scrollbar-width", "none")
                    }
            ) {
                Column(modifier = Modifier.fillMaxWidth().sGap(16)) {
                    SectionHeader(title = t.cardLifeMyStyles, hint = t.cardLifeSelectToGenerate)

                    if (templates.isEmpty()) {
                        SpanText(
                            t.cardLifeNoTemplates,
                            modifier = Modifier
                                .sFontSize(14)
                                .fontWeight(AppTypography.Regular)
                                .color(AppColors.Text.OnSurfaceMuted)
                        )
                    } else {
                        // Selected card | vertical divider | scrollable strip.
                        Row(
                            modifier = Modifier.fillMaxWidth().sGap(12),
                            verticalAlignment = Alignment.Top
                        ) {
                            Column(
                                modifier = Modifier.sGap(8),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                PositionBadge(label = "$nextPosition", large = false)
                                MobileCardArt(
                                    template = selected,
                                    sportName = sportName,
                                    size = PlayerCardSize.Arena,
                                    selectedBorder = true,
                                )
                            }
                            Box(
                                modifier = Modifier
                                    .sWidth(1)
                                    .sHeight(180)
                                    .styleModifier {
                                        property("background", VERTICAL_DIVIDER_GRADIENT)
                                        property("flex-shrink", "0")
                                    }
                            )
                            Div(attrs = {
                                style {
                                    property("flex", "1 1 0")
                                    property("min-width", "0")
                                    property("display", "flex")
                                    property("gap", s(12))
                                    property("align-items", "flex-start")
                                    property("padding-top", s(4))
                                    property("overflow-x", "auto")
                                    property("scrollbar-width", "none")
                                }
                            }) {
                                templates.forEach { template ->
                                    MobileTemplatePreview(
                                        template = template,
                                        sportName = sportName,
                                        isSelected = template.id == selectedTemplateId,
                                        onClick = {
                                            if (template.isOwned) onSelect(template.id)
                                            else onLockedClick(template)
                                        }
                                    )
                                }
                            }
                        }

                        Box(
                            modifier = Modifier.fillMaxWidth(),
                            contentAlignment = Alignment.Center
                        ) {
                            CreatePhotoButton(onClick = onCreatePhoto, enabled = selected != null)
                        }
                    }

                    if (showActiveCards) {
                        SectionDivider()
                        SectionHeader(title = t.cardLifeActiveCards, hint = t.cardLifeDragToReorder)
                        ActiveCardsRow(
                            cards = localOrder,
                            sportName = sportName,
                            onReorder = onReorder,
                            onEditCard = onEditCard,
                            onRequestDelete = onRequestDelete,
                        )
                    }
                }
            }

            // Footer — Cancel discards reorder, Save commits it.
            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                com.arena_web.ui.sections.profile.edit.SheetPillButton(
                    label = t.commonCancel,
                    primary = false,
                    enabled = true,
                    onClick = onCancel,
                    modifier = Modifier.flexGrow(1),
                )
                com.arena_web.ui.sections.profile.edit.SheetPillButton(
                    label = t.commonSave,
                    primary = true,
                    enabled = true,
                    onClick = onSave,
                    modifier = Modifier.flexGrow(1),
                )
            }
        }
    }
}

/** Compact card art for the mobile sheet — sized to a [PlayerCardSize] slot,
 *  optionally framed with the selected blue border. Locked templates dim. */
@Composable
private fun MobileCardArt(
    template: UserCardDto?,
    sportName: String,
    size: PlayerCardSize,
    selectedBorder: Boolean = false,
    locked: Boolean = false,
) {
    Box(
        modifier = Modifier
            .sBorderRadius(14)
            .styleModifier {
                property("flex-shrink", "0")
                property("box-sizing", "border-box")
                property("padding", s(6))
                if (selectedBorder) {
                    property("border", "0.5px solid #0061FF")
                    property(
                        "background",
                        "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(48, 108, 206, 0.2) 100%)"
                    )
                } else {
                    property("border", "0.5px solid transparent")
                }
                if (locked) property("opacity", "0.55")
            },
        contentAlignment = Alignment.Center
    ) {
        if (template != null) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = sportName,
                size = size,
                avatarUrl = null,
                hideRating = true,
                hideShadow = true,
                cardAssetsBasePath = template.assetsBasePath,
                cardTextColor = template.textColor,
            )
        }
    }
}

/** Template tile in the mobile strip — XtraSmall card, lock badge when not owned. */
@Composable
private fun MobileTemplatePreview(
    template: UserCardDto,
    sportName: String,
    isSelected: Boolean,
    onClick: () -> Unit,
) {
    val locked = !template.isOwned
    Box(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("flex-shrink", "0")
                property("position", "relative")
            },
        contentAlignment = Alignment.Center
    ) {
        MobileCardArt(
            template = template,
            sportName = sportName,
            size = PlayerCardSize.XtraSmall,
            selectedBorder = isSelected && !locked,
            locked = locked,
        )
        if (locked) {
            Box(
                modifier = Modifier
                    .sSize(28)
                    .sBorderRadius(100)
                    .styleModifier {
                        property("position", "absolute")
                        property("background", "rgba(11, 22, 46, 0.8)")
                        property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
                        property("backdrop-filter", "blur(4px)")
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText("🔒", modifier = Modifier.sFontSize(13))
            }
        }
    }
}

// =============================================================================
// Header — title + subtitle, with a soft bottom hairline divider.
// =============================================================================
@Composable
private fun HeaderBlock() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(16)
            .sPaddingBottom(16)
    ) {
        SpanText(
            t.cardLifeTitle,
            modifier = Modifier
                .sFontSize(24)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(32)
                .sLetterSpacing(0.24)
                .color(AppColors.Text.Secondary)
        )
        SpanText(
            t.cardLifeSubtitle,
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(18)
                .sLetterSpacing(0.5)
                .color(AppColors.Text.OnSurfaceMuted)
        )
    }
}

// =============================================================================
// Section header — "Label" left, hint right.
// =============================================================================
@Composable
private fun SectionHeader(title: String, hint: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        SpanText(
            title,
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(22)
                .sLetterSpacing(0.09)
                .color(AppColors.Text.Secondary)
        )
        SpanText(
            hint,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .sLetterSpacing(0.028)
                .color(AppColors.Text.OnSurfaceMuted)
        )
    }
}

@Composable
private fun SectionDivider() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(1)
            .styleModifier {
                property("background", SECTION_DIVIDER_GRADIENT)
            }
    )
}

// =============================================================================
// My styles — selected card + Create Photo (left) | divider | template strip (right)
// =============================================================================
@Composable
private fun MyStylesCardSelection(
    templates: List<UserCardDto>,
    profileCards: List<ProfileCardDto>,
    selectedTemplateId: String?,
    sportName: String,
    editingFirstCard: Boolean,
    onSelect: (String) -> Unit,
    onLockedClick: (UserCardDto) -> Unit,
    onCreatePhoto: () -> Unit,
) {
    val selected = templates.firstOrNull { it.id == selectedTemplateId }

    if (templates.isEmpty()) {
        SpanText(
            LocalAppStrings.current.cardLifeNoTemplates,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .color(AppColors.Text.OnSurfaceMuted)
        )
        return
    }

    // Position the resulting card will occupy in the gallery once a photo is
    // confirmed. When the user still has the empty placeholder first card, we
    // edit it in place — even if the template is swapped — so the badge stays
    // at 1. Otherwise the new card lands at the end of the gallery.
    val nextPosition = when {
        editingFirstCard -> 1
        else -> profileCards.size + 1
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(16)
            .sPaddingBottom(22),
        verticalAlignment = Alignment.Top
    ) {
        // Left column — selected preview + Create Photo CTA. Matches Figma
        // 7777:451895 "Card" px-22 + Horizontal Container px-12 = 34px inset
        // from the popup's content area so the selected card breathes away
        // from the popup edge.
        Column(
            modifier = Modifier
                .sGap(32)
                .styleModifier { property("padding", "0 ${s(34)}") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier.sGap(12),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                PositionBadge(label = "$nextPosition", large = true)
                SelectedTemplateCard(template = selected, sportName = sportName)
            }
            CreatePhotoButton(onClick = onCreatePhoto, enabled = selected != null)
        }

        // Vertical divider
        Box(
            modifier = Modifier
                .sWidth(1)
                .sHeight(420)
                .styleModifier {
                    property("background", VERTICAL_DIVIDER_GRADIENT)
                    property("flex-shrink", "0")
                }
        )

        // Right side — owned templates laid out as a 2-row grid that scrolls
        // horizontally when the user owns more than fits in the visible width.
        // Using compose-html Div so we can drive the layout with CSS grid +
        // overflow-x (Kobweb's flex helpers don't natively express grid here).
        Div(attrs = {
            style {
                property("flex", "1 1 0")
                property("min-width", "0")
                property("overflow-x", "auto")
                property("padding-top", s(16))
                property("padding-bottom", s(8))
                property("scrollbar-width", "thin")
            }
        }) {
            Div(attrs = {
                style {
                    property("display", "grid")
                    property("grid-template-rows", "repeat(2, auto)")
                    property("grid-auto-flow", "column")
                    property("grid-auto-columns", "max-content")
                    property("column-gap", s(12))
                    property("row-gap", s(16))
                    property("justify-items", "center")
                }
            }) {
                templates.forEach { template ->
                    TemplatePreview(
                        template = template,
                        sportName = sportName,
                        isSelected = template.id == selectedTemplateId,
                        onClick = {
                            // Owned → promote to big preview.
                            // Locked → bubble up to the parent so it can open
                            // the PurchaseCardModal.
                            if (template.isOwned) onSelect(template.id)
                            else onLockedClick(template)
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun SelectedTemplateCard(template: UserCardDto?, sportName: String) {
    // Compact highlighted card — the visible footprint is 203×308 but we
    // render a properly-sized Large card and scale it via CSS transform so
    // the inner art stays crisp. Frame border + gradient fill remain on the
    // outer Box.
    val scale = 0.8
    val nativeWidth = 254
    val nativeHeight = 385
    Box(
        modifier = Modifier
            .sWidth((nativeWidth * scale).toInt())
            .sHeight((nativeHeight * scale).toInt())
            .styleModifier {
                property("flex-shrink", "0")
                property("position", "relative")
            }
    ) {
        Box(
            modifier = Modifier
                .styleModifier {
                    property("position", "absolute")
                    property("top", "0")
                    property("left", "0")
                    property("width", s(nativeWidth))
                    property("height", s(nativeHeight))
                    property("transform", "scale($scale)")
                    property("transform-origin", "top left")
                    property("box-sizing", "border-box")
                    property("padding", s(8))
                },
            contentAlignment = Alignment.Center
        ) {
            if (template != null) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = sportName,
                    size = PlayerCardSize.Large,
                    avatarUrl = null,
                    hideRating = true,
                    hideShadow = true,
                    cardAssetsBasePath = template.assetsBasePath,
                    cardTextColor = template.textColor,
                )
            }
        }
    }
}

@Composable
private fun TemplatePreview(
    template: UserCardDto,
    sportName: String,
    isSelected: Boolean,
    onClick: () -> Unit,
) {
    // Tile sized so three fit per row inside the 2-row scrollable grid.
    //   • Owned: full-opacity card, click promotes it to the big preview.
    //   • Locked (!isOwned): dimmed card with a lock badge + a price tag
    //     underneath — click opens the purchase popup.
    val locked = !template.isOwned
    Column(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .sBorderRadius(14)
            .styleModifier {
                property("flex-shrink", "0")
                property("box-sizing", "border-box")
                // Always reserve a 1px border slot so toggling the selection
                // colour doesn't shift neighbouring tiles. Transparent by
                // default; colours up only for the picked owned template.
                if (isSelected && !locked) {
                    property("border", "1px solid #0c42ff")
                    property(
                        "background",
                        "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(48, 108, 206, 0.12) 100%)"
                    )
                } else {
                    property("border", "1px solid transparent")
                }
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(
            modifier = Modifier
                .sWidth(130)
                .sHeight(196)
                .styleModifier {
                    property("box-sizing", "border-box")
                    property("position", "relative")
                },
            contentAlignment = Alignment.Center
        ) {
            // Card art — dimmed when locked.
            Box(
                modifier = Modifier.styleModifier {
                    if (locked) property("opacity", "0.55")
                },
                contentAlignment = Alignment.Center
            ) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = sportName,
                    size = PlayerCardSize.Arena,
                    avatarUrl = null,
                    hideRating = true,
                    hideShadow = true,
                    cardAssetsBasePath = template.assetsBasePath,
                    cardTextColor = template.textColor,
                )
            }
            if (locked) {
                // Centered lock badge — same dark pill the Card Shop uses.
                Box(
                    modifier = Modifier
                        .sSize(36)
                        .sBorderRadius(100)
                        .styleModifier {
                            property("position", "absolute")
                            property("background", "rgba(11, 22, 46, 0.8)")
                            property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
                            property("backdrop-filter", "blur(4px)")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText("🔒", modifier = Modifier.sFontSize(16))
                }
            }
        }
    }
}

// =============================================================================
// Position badge — circular indicator with a soft glass surface and a thin
// translucent stroke. Used both for the "next slot" indicator above the
// selected style and the per-card display-order badges in the gallery row.
// =============================================================================
@Composable
private fun PositionBadge(label: String, large: Boolean) {
    val size = if (large) 40 else 24
    val fontSize = if (large) 24 else 18
    val lineHeight = if (large) 32 else 22
    val borderWidth = if (large) "0.6px" else "0.3px"
    Box(
        modifier = Modifier
            .sSize(size)
            .sBorderRadius(100)
            .styleModifier {
                property("background", BADGE_BG)
                property("border", "$borderWidth solid rgba(255, 255, 255, 0.4)")
                property("box-sizing", "border-box")
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(fontSize)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(lineHeight)
                .sLetterSpacing(if (large) 0.24 else 0.09)
                .color(AppColors.Text.Secondary)
        )
    }
}

// =============================================================================
// Active profile cards — drag-to-reorder row.
// =============================================================================
@Composable
private fun ActiveCardsRow(
    cards: List<ProfileCardDto>,
    sportName: String,
    onReorder: (List<ProfileCardDto>) -> Unit,
    onEditCard: (ProfileCardDto) -> Unit = {},
    onRequestDelete: (ProfileCardDto) -> Unit = {},
) {
    // `draggingVisual` drives the dimmed-while-dragging style (needs recompose);
    // `live` mirrors the latest values for the native touch listeners, which
    // capture once on mount and would otherwise read stale state.
    var draggingVisual by remember { mutableStateOf<String?>(null) }
    val live = remember {
        object {
            var draggingId: String? = null
            var cards: List<ProfileCardDto> = emptyList()
            var onReorder: (List<ProfileCardDto>) -> Unit = {}
            var rowEl: HTMLElement? = null
            // The lifted card that follows the finger, and the finger's offset
            // within it at grab time (so it stays put under the touch point).
            var dragNode: HTMLElement? = null
            var grabDX: Double = 0.0
            var grabDY: Double = 0.0
            var pointerX: Double = 0.0
            var pointerY: Double = 0.0
        }
    }
    live.cards = cards
    live.onReorder = onReorder

    // Move the dragged card to the hovered card's slot — an instant array swap.
    fun moveDraggedOver(targetId: String) {
        val dragged = live.draggingId ?: return
        if (dragged == targetId) return
        val list = live.cards
        val fromIdx = list.indexOfFirst { it.id == dragged }
        val toIdx = list.indexOfFirst { it.id == targetId }
        if (fromIdx < 0 || toIdx < 0) return
        val reordered = list.toMutableList()
        reordered.add(toIdx, reordered.removeAt(fromIdx))
        live.onReorder(reordered)
    }

    // After every reorder the slot contents swap in place (no key, no FLIP). The
    // cards just take their new slots instantly; the only thing we carry over is
    // the lifted card — re-bind it to whichever slot now holds it and keep it
    // pinned under the finger, so the drag stays continuous through the swap.
    val orderKey = cards.joinToString("|") { it.id }
    LaunchedEffect(orderKey) {
        val rowEl = live.rowEl ?: return@LaunchedEffect
        val nodes = rowEl.asDynamic().querySelectorAll("[data-reorder-id]")
        val n = (nodes.length as Number).toInt()
        for (i in 0 until n) {
            val node: HTMLElement = nodes[i]
            val id = node.getAttribute("data-reorder-id")
            val st = node.style.asDynamic()
            if (live.dragNode != null && id != null && id == live.draggingId) {
                live.dragNode = node
                st.transition = "none"
                val r = node.getBoundingClientRect()
                val tx = (live.pointerX - live.grabDX) - (r.left as Number).toDouble()
                val ty = (live.pointerY - live.grabDY) - (r.top as Number).toDouble()
                st.transform = "translate(${tx}px, ${ty}px)"
                st.zIndex = "30"
                st.pointerEvents = "none"
                st.boxShadow = "0 14px 30px rgba(0, 0, 0, 0.5)"
            } else {
                // Plain card — drop any leftover drag styling.
                st.transition = "none"
                st.transform = "none"
                st.boxShadow = ""
                st.zIndex = ""
                st.pointerEvents = ""
            }
        }
    }

    // Native HTML5 drag-and-drop above only fires for mouse (desktop). On touch
    // we run our own pointer-based reorder at the row level via elementFromPoint,
    // and stopPropagation so the BottomSheet's swipe-to-dismiss doesn't steal it.
    Div(attrs = {
        style {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("width", "100%")
            property("gap", s(8))
            property("align-items", "flex-start")
        }
        ref { el ->
            live.rowEl = el
            fun pointOf(e: Event): Pair<Double, Double> {
                val touches = e.asDynamic().touches
                val t = if (touches != null && touches.length > 0) touches[0]
                else e.asDynamic().changedTouches[0]
                return (t.clientX as Number).toDouble() to (t.clientY as Number).toDouble()
            }
            fun cardNodeAt(x: Double, y: Double): dynamic {
                val node = document.elementFromPoint(x, y) ?: return null
                // Controls flagged data-no-drag (the edit button + its menu) must
                // stay tappable — don't let a touch on them start a card drag.
                if (node.asDynamic().closest("[data-no-drag]") != null) return null
                return node.asDynamic().closest("[data-reorder-id]")
            }
            // Drop target by layout rect (robust where elementFromPoint is flaky —
            // gaps between cards, the lifted card on top). Excludes the dragged one,
            // whose own rect is offset by its finger-follow transform.
            fun targetCardAt(x: Double, y: Double, exclude: String?): String? {
                val r = live.rowEl ?: return null
                val nodes = r.asDynamic().querySelectorAll("[data-reorder-id]")
                val count = (nodes.length as Number).toInt()
                for (i in 0 until count) {
                    val node = nodes[i]
                    val id = node.getAttribute("data-reorder-id") as? String ?: continue
                    if (id == exclude) continue
                    val rect = node.getBoundingClientRect()
                    val left = (rect.left as Number).toDouble()
                    val top = (rect.top as Number).toDouble()
                    val right = (rect.right as Number).toDouble()
                    val bottom = (rect.bottom as Number).toDouble()
                    if (x >= left && x <= right && y >= top && y <= bottom) return id
                }
                return null
            }
            val ts: (Event) -> Unit = { e ->
                val (x, y) = pointOf(e)
                val cardNode = cardNodeAt(x, y)
                val id = cardNode?.getAttribute("data-reorder-id") as? String
                if (id != null) {
                    val node: HTMLElement = cardNode
                    live.draggingId = id
                    draggingVisual = id
                    live.dragNode = node
                    val r = node.getBoundingClientRect()
                    live.grabDX = x - (r.left as Number).toDouble()
                    live.grabDY = y - (r.top as Number).toDouble()
                    // Lift: raise + shadow (the "selected" cue — no dimming), and
                    // disable pointer-events so elementFromPoint sees cards beneath.
                    val st = node.style.asDynamic()
                    st.transition = "box-shadow 140ms ease"
                    st.zIndex = "30"
                    st.pointerEvents = "none"
                    st.willChange = "transform"
                    st.boxShadow = "0 14px 30px rgba(0, 0, 0, 0.5)"
                    e.stopPropagation()
                }
            }
            val tm: (Event) -> Unit = { e ->
                val node = live.dragNode
                if (live.draggingId != null && node != null) {
                    e.preventDefault()
                    e.stopPropagation()
                    val (x, y) = pointOf(e)
                    live.pointerX = x
                    live.pointerY = y
                    // Live swap: the moment the finger is over another card, reorder
                    // — that card FLIP-slides out of the way (handled post-commit in
                    // the LaunchedEffect, which also re-pins the floating card).
                    val targetId = targetCardAt(x, y, live.draggingId)
                    if (targetId != null) moveDraggedOver(targetId)
                    // Smooth-follow the current floating node between swaps.
                    val st = node.style.asDynamic()
                    st.transition = "none"
                    st.transform = "none"
                    val r = node.getBoundingClientRect()
                    val tx = (x - live.grabDX) - (r.left as Number).toDouble()
                    val ty = (y - live.grabDY) - (r.top as Number).toDouble()
                    st.transform = "translate(${tx}px, ${ty}px)"
                }
            }
            val te: (Event) -> Unit = { e ->
                val node = live.dragNode
                if (live.draggingId != null && node != null) {
                    e.stopPropagation()
                    // The order is already committed (live swap). Just settle: glide
                    // the floating card from the finger into its current slot and
                    // drop the lift cue. NB: never touch `position` — it's the
                    // positioning context for the drag-handle icon.
                    val st = node.style.asDynamic()
                    st.transition = "transform 200ms cubic-bezier(0.2, 0, 0, 1), box-shadow 200ms ease"
                    st.transform = "none"
                    st.boxShadow = "none"
                    window.setTimeout({
                        st.zIndex = ""
                        st.pointerEvents = ""
                        st.willChange = ""
                        st.transition = ""
                    }, 220)
                }
                live.draggingId = null
                draggingVisual = null
                live.dragNode = null
            }
            el.addEventListener("touchstart", ts)
            el.addEventListener("touchmove", tm, js("({ passive: false })"))
            el.addEventListener("touchend", te)
            el.addEventListener("touchcancel", te)
            onDispose {
                el.removeEventListener("touchstart", ts)
                el.removeEventListener("touchmove", tm)
                el.removeEventListener("touchend", te)
                el.removeEventListener("touchcancel", te)
                live.rowEl = null
            }
        }
    }) {
        // Fixed per-slot rendering (no key): the position badges stay put while
        // only the card art inside each slot lifts and reorders. The FLIP below
        // animates the card-art swap by data-reorder-id (content swaps in place).
        cards.forEachIndexed { index, card ->
            DraggableActiveCard(
                card = card,
                index = index,
                sportName = sportName,
                onDragStart = { live.draggingId = card.id; draggingVisual = card.id },
                onDragEnd = { live.draggingId = null; draggingVisual = null },
                onDragOver = { moveDraggedOver(card.id) },
                onEdit = { onEditCard(card) },
                onRequestDelete = { onRequestDelete(card) },
            )
        }
    }
}

@Composable
private fun DraggableActiveCard(
    card: ProfileCardDto,
    index: Int,
    sportName: String,
    onDragStart: () -> Unit,
    onDragEnd: () -> Unit,
    onDragOver: () -> Unit,
    onEdit: () -> Unit = {},
    onRequestDelete: () -> Unit = {},
) {
    var menuOpen by remember(card.id) { mutableStateOf(false) }
    // Outer slot — FIXED. The position number lives here and never moves; only
    // the card art inside lifts/reorders.
    Div(attrs = {
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("gap", s(2))
            property("width", s(133))
        }
    }) {
        // Position badge centered above the card (stays put).
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier { property("padding", "0 ${s(54)}") },
            contentAlignment = Alignment.Center
        ) {
            PositionBadge(label = "${index + 1}", large = false)
        }

        // Card art — the draggable / liftable / FLIP unit (data-reorder-id). The
        // touch handlers lift only this; HTML5 drag events cover desktop mouse.
        Div(attrs = {
            attr("draggable", "true")
            attr("data-reorder-id", card.id)
            style {
                property("border-radius", s(16))
                property("padding", "${s(12)} ${s(8)}")
                property("position", "relative")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("cursor", "grab")
                property("touch-action", "none")
                property("will-change", "transform")
            }
            onDragStart { onDragStart() }
            onDragEnd { onDragEnd() }
            onDragEnter { it.preventDefault(); onDragOver() }
            onDragOver { it.preventDefault() }
        }) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = sportName,
                size = PlayerCardSize.Arena,
                avatarUrl = card.avatarUrl,
                hideRating = true,
                hideShadow = true,
                cardAssetsBasePath = card.assetsBasePath,
                cardTextColor = card.textColor,
            )
            // Drag-handle marker — 6-dot grid (Figma 7777:451944). Nudged toward
            // the card edge (right 10) per the latest design pass.
            Icon(
                src = AppIcons.DragHandle,
                modifier = Modifier
                    .sWidth(10)
                    .sHeight(14)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", s(0))
                        property("right", s(10))
                    },
            )

            // Edit button (left, same level as the drag handle) → dropdown with
            // Edit / Delete. Flagged data-no-drag so taps don't start a reorder
            // drag (the row's touch handler skips data-no-drag subtrees).
            Div(attrs = {
                attr("data-no-drag", "true")
                attr("draggable", "false")
                style {
                    property("position", "absolute")
                    property("top", s(0))
                    property("left", s(10))
                    property("z-index", "3")
                }
            }) {
                Box(
                    modifier = Modifier
                        .sSize(20)
                        .cursor(Cursor.Pointer)
                        .onClick { it.stopPropagation(); menuOpen = !menuOpen },
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(src = AppIcons.Pencil, modifier = Modifier.sWidth(14).sHeight(14))
                }

                if (menuOpen) {
                    CardActionMenu(
                        onEdit = { menuOpen = false; onEdit() },
                        onDelete = { menuOpen = false; onRequestDelete() },
                        onDismiss = { menuOpen = false },
                    )
                }
            }
        }
    }
}

// Small dropdown anchored under the edit pencil — Edit + Delete (destructive).
// Wrapped (with its dismiss scrim) in data-no-drag so it never starts a drag.
@Composable
private fun CardActionMenu(
    onEdit: () -> Unit,
    onDelete: () -> Unit,
    onDismiss: () -> Unit,
) {
    // Full-screen scrim to catch outside taps. Fixed so it covers the viewport.
    Div(attrs = {
        attr("data-no-drag", "true")
        style {
            property("position", "fixed")
            property("inset", "0")
            property("z-index", "20")
        }
        onClick { it.stopPropagation(); onDismiss() }
    }) {}

    Column(
        modifier = Modifier
            .sBorderRadius(12)
            .styleModifier {
                property("position", "absolute")
                property("top", s(24))
                property("left", "0")
                property("z-index", "21")
                property("min-width", s(132))
                property("background", "#0F1A33")
                property("border", "0.5px solid rgba(255, 255, 255, 0.12)")
                property("box-shadow", "0 ${s(8)} ${s(24)} rgba(0, 0, 0, 0.5)")
                property("overflow", "hidden")
            },
    ) {
        CardActionMenuItem(label = LocalAppStrings.current.commonEdit, iconPath = AppIcons.Pencil, destructive = false, onClick = onEdit)
        Box(
            modifier = Modifier.fillMaxWidth().sHeight(1).styleModifier {
                property("background", "rgba(255, 255, 255, 0.08)")
            },
        )
        CardActionMenuItem(label = LocalAppStrings.current.commonDelete, iconPath = AppIcons.Trash, destructive = true, onClick = onDelete)
    }
}

@Composable
private fun CardActionMenuItem(
    label: String,
    iconPath: String,
    destructive: Boolean,
    onClick: () -> Unit,
) {
    val color = if (destructive) "#FF4242" else "#BED6F5"
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .onClick { it.stopPropagation(); onClick() }
            .sGap(10)
            .styleModifier { property("padding", "${s(10)} ${s(14)}") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .sSize(16)
                .styleModifier {
                    property("background-color", color)
                    property("mask", "url('$iconPath') no-repeat center / contain")
                    property("-webkit-mask", "url('$iconPath') no-repeat center / contain")
                },
        )
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .styleModifier { property("color", color) },
        )
    }
}

// =============================================================================
// Footer — single Close pill (Save removed; reorder auto-flushes on dismiss).
// =============================================================================
@Composable
private fun FooterButtons(onClose: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().sGap(12),
        horizontalArrangement = Arrangement.End,
        verticalAlignment = Alignment.CenterVertically
    ) {
        OutlinedPillButton(label = LocalAppStrings.current.commonClose, onClick = onClose)
    }
}

@Composable
private fun OutlinedPillButton(label: String, onClick: () -> Unit) {
    // 46px pill — translucent blue fill with the design-system gradient ring
    // (mask-composite via `GradientBorderRing`) so the border wraps the full
    // shape and matches every other outlined pill in the app.
    Box(
        modifier = Modifier
            .sHeight(46)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
                property(
                    "background",
                    AppGradients.InteractiveSecondary
                )
            },
        contentAlignment = Alignment.Center
    ) {
        GradientBorderRing(radiusPx = 100)
        Box(
            modifier = Modifier.styleModifier {
                property("padding", "${s(14)} ${s(32)}")
            },
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(AppColors.Text.Secondary)
            )
        }
    }
}

// =============================================================================
// Create Photo CTA — primary blue pill with sparkle icon and label.
// =============================================================================
@Composable
private fun CreatePhotoButton(onClick: () -> Unit, enabled: Boolean) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .sHeight(46)
            .then(if (enabled) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            .sBorderRadius(100)
            .styleModifier {
                property(
                    "background",
                    sportColors.primary.toRgb().let {
                        "rgb(${it.red}, ${it.green}, ${it.blue})"
                    }
                )
                property("padding", "${s(14)} ${s(32)} ${s(14)} ${s(28)}")
                if (!enabled) property("opacity", "0.5")
            },
        contentAlignment = Alignment.Center
    ) {
        Row(
            modifier = Modifier.sGap(8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(src = AppIcons.Sparkle, modifier = Modifier.sSize(24))
            SpanText(
                LocalAppStrings.current.cardLifeCreatePhoto,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .sLetterSpacing(0.16)
                    .color(sportColors.onPrimary)
            )
        }
    }
}

// =============================================================================
// Delete-card confirmation — bottom sheet on mobile, centred popup on desktop.
// Triggered from the card Edit ▸ Delete action; confirming removes the card from
// the gallery (the purchased template stays in "My styles").
// =============================================================================
@Composable
private fun DeletePhotoConfirm(
    target: ProfileCardDto?,
    mobile: Boolean,
    onConfirm: (ProfileCardDto) -> Unit,
    onCancel: () -> Unit,
) {
    val card = target ?: return
    if (mobile) {
        BottomSheet(onDismiss = onCancel) {
            DeletePhotoConfirmBody(onConfirm = { onConfirm(card) }, onCancel = onCancel)
        }
    } else {
        Box(
            modifier = Modifier
                .position(Position.Fixed)
                .top(0.px).left(0.px).right(0.px).bottom(0.px)
                .zIndex(1060)
                .styleModifier { property("background", "rgba(0, 0, 0, 0.65)") }
                .onClick { onCancel() },
            contentAlignment = Alignment.Center,
        ) {
            Column(
                modifier = Modifier
                    .sMaxWidth(440)
                    .fillMaxWidth()
                    .sMargin(16)
                    .sBorderRadius(26)
                    .sPadding(24)
                    .onClick { it.stopPropagation() }
                    .styleModifier {
                        property("background", DIALOG_BG)
                        property("overflow", "hidden")
                    },
            ) {
                DeletePhotoConfirmBody(onConfirm = { onConfirm(card) }, onCancel = onCancel)
            }
        }
    }
}

@Composable
private fun DeletePhotoConfirmBody(onConfirm: () -> Unit, onCancel: () -> Unit) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sGap(20),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier = Modifier
                .sSize(56)
                .sBorderRadius(100)
                .styleModifier { property("background", "rgba(255, 66, 66, 0.12)") },
            contentAlignment = Alignment.Center,
        ) {
            Box(
                modifier = Modifier
                    .sSize(24)
                    .styleModifier {
                        property("background-color", "#FF4242")
                        property("mask", "url('${AppIcons.Trash}') no-repeat center / contain")
                        property("-webkit-mask", "url('${AppIcons.Trash}') no-repeat center / contain")
                    },
            )
        }
        Column(
            modifier = Modifier.fillMaxWidth().sGap(8),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.cardLifeDeleteCardTitle,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(26)
                    .sLetterSpacing(0.2)
                    .color(AppColors.Text.Secondary)
                    .styleModifier { property("text-align", "center") },
            )
            SpanText(
                t.cardLifeDeleteCardMessage,
                modifier = Modifier
                    .fillMaxWidth()
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(22)
                    .color(AppColors.Text.OnSurfaceMuted)
                    .styleModifier { property("text-align", "center") },
            )
        }
        Row(
            modifier = Modifier.fillMaxWidth().sGap(12),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .flexGrow(1)
                    .sHeight(46)
                    .sBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .onClick { onCancel() }
                    .styleModifier {
                        property("position", "relative")
                        property("background", AppGradients.InteractiveSecondary)
                    },
                contentAlignment = Alignment.Center,
            ) {
                GradientBorderRing(radiusPx = 100)
                SpanText(
                    t.commonCancel,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(18)
                        .sLetterSpacing(0.032)
                        .color(AppColors.Text.Secondary),
                )
            }
            Box(
                modifier = Modifier
                    .flexGrow(1)
                    .sHeight(46)
                    .sBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .onClick { onConfirm() }
                    .backgroundColor(Color("#FF4242")),
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    t.commonDelete,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(18)
                        .sLetterSpacing(0.032)
                        .color(Color.white),
                )
            }
        }
    }
}
